import { Injectable } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { Branch, BranchRoom, Reservation } from '../models/branch';
import * as Parse from 'parse';
import { ERROR_MSG } from '../shared/errorMessages';
import { User } from '../models/user';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root'
})
export class BranchService {

  branches: BehaviorSubject<Branch[]> = new BehaviorSubject([]);

  isWaiter: BehaviorSubject<Boolean> = new BehaviorSubject(null);
  waiterAvailable: BehaviorSubject<Boolean> = new BehaviorSubject(null);

  currentBranch: BehaviorSubject<Branch> = new BehaviorSubject(null);
  tableCalls: BehaviorSubject<any> = new BehaviorSubject({});
  private userBranches: BehaviorSubject<any[]> = new BehaviorSubject([]);

  private userBranchesSubscription: Subscription = null;
  private currentBranchSubscription: Subscription = null;

  private liveQueryUserBranch: Parse.LiveQuerySubscription = null;;
  private liveQueryBranch: Parse.LiveQuerySubscription = null;;
  private liveQueryBranchTables: Parse.LiveQuerySubscription = null;;

  private branchesCollection = Parse.Object.extend('Branch');
  private userBranchesCollection = Parse.Object.extend('UserBranch');
  private branchRoomsCollection = Parse.Object.extend('BranchRoom');
  private roomTablesCollection = Parse.Object.extend('RoomTable');
  private reservationCollection = Parse.Object.extend('Reservation');
  private settingsCollection = Parse.Object.extend('Settings');
  private branchWaitersCollection = Parse.Object.extend('BranchWaiter');
  private waiterRoomCollection = Parse.Object.extend('WaiterRoom');
  private branchDriversCollection = Parse.Object.extend('BranchDriver');
  private branchSchedulesCollection = Parse.Object.extend('BranchSchedule');

  constructor(private authService: AuthService) { }


  async stopLiveQueryBranches(): Promise<void> {
    if (this.liveQueryUserBranch) {
      this.liveQueryUserBranch.unsubscribe();
    }
    if (this.liveQueryBranch) {
      this.liveQueryBranch.unsubscribe();
    }
    if (this.userBranchesSubscription) {
      this.userBranchesSubscription.unsubscribe();
    }
  }

  async startLiveQueryUserBranches(user: Parse.User): Promise<void>{
    let query = new Parse.Query(this.userBranchesCollection);
    query.equalTo('user', user);

    this.liveQueryUserBranch = await query.subscribe();

    this.liveQueryUserBranch.on('open', async () => {
      this.userBranches.next(await query.findAll());
    });

    this.liveQueryUserBranch.on('enter', (userB) => {
      let userBranches = this.userBranches.value;
      userBranches.push(userB);
      this.userBranches.next(userBranches);
    });

    this.liveQueryUserBranch.on('create', (userB) => {
      let userBranches = this.userBranches.value;
      userBranches.push(userB);
      this.userBranches.next(userBranches);
    });

    this.liveQueryUserBranch.on('leave', (userB) => {
      let userBranches = this.userBranches.value;
      userBranches = userBranches.filter(ub => ub.id != userB.id);
      this.userBranches.next(userBranches);
    });

    this.liveQueryUserBranch.on('delete', (userB) => {
      let userBranches = this.userBranches.value;
      userBranches = userBranches.filter(ub => ub.id != userB.id);
      if(userBranches.length == 0){
        this.authService.logout();
      }
      this.userBranches.next(userBranches);
    });
  }

  async startLiveQueryCurrentBranch(): Promise<void> {
    this.userBranchesSubscription = this.userBranches.subscribe(async (userBranches) => {
      if (this.liveQueryBranch) {
        await this.liveQueryBranch.unsubscribe();
      }

      let query = new Parse.Query(this.branchesCollection);
      let bs = userBranches.map(b => b.get('branch').id);
      query.containedIn('objectId', bs);
      query.equalTo('deleted', false);

      this.liveQueryBranch = await query.subscribe();

      this.liveQueryBranch.on('open', async () => {
        let branches = (await query.findAll()).map((b) => {
          return b.toJSON() as any;
        });
        this.branches.next(branches);
        if (branches.length > 0 && !this.currentBranch) {
          this.setCurrentBranch(branches[0]);
        }
      });

      this.liveQueryBranch.on('enter', (branch) => {
        let b = branch.toJSON() as any;
        let found = false;
        let branches = this.branches.value;
        for (let bb of branches) {
          if (bb.objectId == b.objectId) {
            found = true;
            break;
          }
        }
        if (!found) {
          branches.push(b);
          this.branches.next(branches);
          if (this.currentBranch.value == null) {
            this.setCurrentBranch(branches[0]);
          }
        }
      });

      this.liveQueryBranch.on('leave', (branch) => {
        let branches = this.branches.value;
        branches = branches.filter(b => b.objectId != branch.id);
        this.branches.next(branches);
        if (branch.id == this.currentBranch.value.objectId) {
          if (branches.length > 0) {
            this.setCurrentBranch(branches[0]);
          } else {
            this.setCurrentBranch(null);
            this.authService.logout();
          }
        }
      });
    });
  }

  async startLiveQueryBranches(): Promise<void> {
    let user = await Parse.User.currentAsync();

    if (user) {
      await this.startLiveQueryUserBranches(user);
      await this.startLiveQueryCurrentBranch();
      await this.asignIsWaiterAndAvailability();
    }
  }

  async asignIsWaiterAndAvailability(): Promise<void> {

    this.currentBranch.subscribe(branch => {
      if (branch) {
        let user = Parse.User.current();
        // branch Waiter Query
        let branchWaitersQuery = new Parse.Query(this.branchWaitersCollection);
        branchWaitersQuery.containedIn('branch', [branch.objectId]);
        branchWaitersQuery.containedIn('waiter', [user.id]);

        branchWaitersQuery.first().then(async branchWaiter => {
          if (branchWaiter) {
            // waiter Room query
            let waiterRoomsQuery = new Parse.Query(this.waiterRoomCollection);
            waiterRoomsQuery.equalTo('waiter', branchWaiter);

            let res = (await waiterRoomsQuery.findAll()).some(schedule => {
              return this._validateSchedule(schedule.get('start'), schedule.get('end'), schedule.get('day'))
            });
            this.isWaiter.next(true);
            this.waiterAvailable.next(res);
          } else {
            this.isWaiter.next(false);
            this.waiterAvailable.next(false);
          }
        });
      }
    })
  }

  setCurrentBranch(branch: Branch): void {
    this.currentBranch.next(branch);
  }

  async getCurrentBranchTables(): Promise<any> {
    let branch = this.currentBranch.value;
    if (branch) {
      return Parse.Cloud.run('getBranchRooms', { branch: branch.objectId }).then(res => {
        if (res.success) {
          res.data = res.data.map(obj => obj.toJSON());
        }
        return res;
      });
    } else {
      // no branch selected
      return [];
    }
  }

  _validateSchedule(start, end, day) {
    const now = new Date();
    let startDate = new Date(now.toDateString() + ' ' + start);
    let endDate = new Date(now.toDateString() + ' ' + end);
    let res = now.getDay() == day && startDate <= now && endDate >= now;
    return res;
  }

  async getUserAssignedTables(): Promise<any> {
    let branch = this.currentBranch.value;
    if (branch) {
      if (this.authService.userRoles.value.some(role => role.get('name') == 'Mesero')) {
        // if user is waiter return assigned rooms

        // get relationship between user and branch
        let branchWaiterQuery = new Parse.Query(this.branchWaitersCollection);
        branchWaiterQuery.containedIn('waiter', [this.authService.currentUser.value.objectId]);
        branchWaiterQuery.containedIn('branch', [branch.objectId]);
        branchWaiterQuery.equalTo('deleted', false);

        // get existent rooms
        let branchRoomQuery = new Parse.Query(this.branchRoomsCollection);
        branchRoomQuery.containedIn('branch', [branch.objectId]);
        branchRoomQuery.equalTo('deleted', false);

        // get existent rooms assigned to waiter
        let waiterRoomQuery = new Parse.Query(this.waiterRoomCollection);
        waiterRoomQuery.matchesQuery('waiter', branchWaiterQuery);
        waiterRoomQuery.matchesQuery('room', branchRoomQuery);

        let assignedRooms = (await waiterRoomQuery.find()).filter(assign => this._validateSchedule(assign.get('start'), assign.get('end'), assign.get('day')));

        // get tables from assigned rooms
        let tableQuery = new Parse.Query(this.roomTablesCollection);
        tableQuery.containedIn('room', assignedRooms.map(assigned => assigned.get('room')));
        tableQuery.equalTo('deleted', false);
        tableQuery.include('room');

        let tables = (await tableQuery.find()).map(t => t.toJSON() as any);

        let rooms = {};
        for (let table of tables) {
          if (rooms[table.room.objectId] == undefined) {
            rooms[table.room.objectId] = table.room;
            rooms[table.room.objectId].tables = [];
          }
          rooms[table.room.objectId].tables.push(table);
        }
        return { success: true, data: Object.values(rooms) };
      } else {
        // else return all rooms
        return this.getCurrentBranchTables();
      }
    } else {
      // no branch selected
      return [];
    }
  }

  async createBranchRoom(name: string, tables: number): Promise<any> {
    let branch = this.currentBranch.value;
    if (branch) {
      let parseBranch = new this.branchesCollection();
      parseBranch.id = branch.objectId;

      let room = new this.branchRoomsCollection();
      room.set('branch', parseBranch);
      room.set('name', name);

      let newObjects = [];
      newObjects.push(room);

      for (let i = 1; i <= tables; i++) {
        let table = new this.roomTablesCollection();
        table.set('room', room);
        table.set('number', i);

        newObjects.push(table);
      }

      return Parse.Object.saveAll(newObjects).then((res) => {
        return { success: true, data: res };
      }).catch((error) => {
        return { success: false, data: ERROR_MSG['SERVER_ERROR'] };
      });
    } else {
      // no branch selected
      return { success: false, data: ERROR_MSG['BRANCH_NOT_FOUND'] };
    }
  }

  async deleteRoom(roomId: string): Promise<any> {
    let query = new Parse.Query(this.branchRoomsCollection);

    let room = await (query.get(roomId));
    room.set('deleted', true);

    return room.save().then((res) => {
      let waiterRoomQuery = new Parse.Query(this.waiterRoomCollection);
      waiterRoomQuery.containedIn('room', [roomId]);
      return waiterRoomQuery.findAll().then(waiterRooms => {
        Parse.Object.destroyAll(waiterRooms);
        return { success: true };
      }, () => {
      })
    }).catch((error) => {
      return { success: false, data: ERROR_MSG['SERVER_ERROR'] };
    });
  }

  async updateRoom(roomId: string, name: string, tables: number): Promise<any> {
    let roomQuery = new Parse.Query(this.branchRoomsCollection);
    let room = await (roomQuery.get(roomId));

    let tableQuery = new Parse.Query(this.roomTablesCollection);
    tableQuery.equalTo('room', room);
    tableQuery.ascending('number');

    let updatedObjects = [];

    room.set('name', name);

    updatedObjects.push(room);

    // to create tables, first undelete existing ones
    let ts = await tableQuery.find();

    let idx = 0;
    for (; idx < ts.length && idx < tables; idx++) {
      ts[idx].set('deleted', false);
      updatedObjects.push(ts[idx]);
    }

    if (idx < tables) {
      // need to create new tables
      for (; idx < tables; idx++) {
        let t = new this.roomTablesCollection();
        t.set('room', room);
        t.set('number', idx + 1);
        updatedObjects.push(t);
      }
    } else {
      // the rest of tables should be deleted
      for (; idx < ts.length; idx++) {
        ts[idx].set('deleted', true);
        updatedObjects.push(ts[idx]);
      }
    }

    return Parse.Object.saveAll(updatedObjects).then((res) => {
      return { success: true, data: res };
    }).catch((error) => {
      return { success: false, data: ERROR_MSG['SERVER_ERROR'] };
    });
  }

  /**
   * verify if the date of the reservation is allowed for the table and make the reservation
   * @param reservation infomation of the reservation, include {table: objectId}, {client}, amountOfPeople and date for the reservation
   * @returns {Promise} true with the object if the reservation was succeful created, else returns false with text error.
   */
  async createReservation(reservation: Reservation): Promise<any> {
    return Parse.Cloud.run('createReservation', { reservation: reservation }).then(result => { return result });
  }

  /**
   * get all open reservations with table and room information.
   * @returns get All open reservations for the current branch.
   */
  async getOpenReservations(): Promise<any> {
    let branch = this.currentBranch.value;
    if (branch) {
      let branchRoomQuery = new Parse.Query(this.branchRoomsCollection);
      branchRoomQuery.containedIn('branch', [branch.objectId]);
      branchRoomQuery.equalTo('deleted', false);

      let roomTablesQuery = new Parse.Query(this.roomTablesCollection);
      roomTablesQuery.equalTo('deleted', false);
      roomTablesQuery.matchesKeyInQuery('room', 'objectId', branchRoomQuery);

      let reservationQuery = new Parse.Query(this.reservationCollection);
      reservationQuery.equalTo('taken', false);
      reservationQuery.matchesKeyInQuery('table', 'objectId', roomTablesQuery);
      reservationQuery.include('table.room');

      let reservations = [];
      (await reservationQuery.findAll()).forEach(reservation => {
        reservations.push(reservation.toJSON());
      });
      return { success: true, data: reservations };
    }
    else {
      return { success: false, data: ERROR_MSG['BRANCH_NOT_FOUND'] };
    }
  }

  /**
   * take the reservation changing the taken flag
   * @param reservationObjectId reservation id to change the taken value
   * @returns {Promise} a Promise with the object changed if was succefull updated.
   */
  async takeReservation(reservationObjectId: string): Promise<any> {
    let query = new Parse.Query(this.reservationCollection);
    return query.get(reservationObjectId).then(reservation => {
      if (reservation) {
        return reservation.save({ taken: true }).then(response => {
          return { success: true, data: response.toJSON() };
        }, () => {
          return { success: false, data: ERROR_MSG['RESERVATION_CANT_SAVE'] }
        })
      }
      else {
        return { success: false, data: ERROR_MSG['RESERVATION_NOT_FOUND'] };
      }
    })
  }

  /**
   * call a cloud function to delete an reservation
   * @param reservationObjectId reservation id delete
   * @returns {Promise} a Promise with the object deleted
   */
  async deleteReservation(reservationObjectId: string): Promise<any> {
    return Parse.Cloud.run('deleteReservation', { reservationId: reservationObjectId }).then(result => { return result });
  }

  /**
   * Update Client, date and amountOfPeople of an reservation.
   * @param reservation reservation Object that was already created.
   * @returns {Promise} a Promise with the object changed if was succefull updated.
   */
  async updateReservation(reservation: Reservation): Promise<any> {
    return Parse.Cloud.run('updateReservation', { reservation: reservation }).then(result => { return result });
  }

  /**
   * Call to cloud function to create a Waiter.
   * @param newUser User object that contains information for the new waiter
   * @returns waiter object created
   */
  async createWaiter(newUser: User): Promise<any> {
    let branch = this.currentBranch.value;
    if (branch) {
      return Parse.Cloud.run('createWaiter', { newUser: newUser, branch: branch }).then(result => {
        return result;
      })
    }
    else {
      return { success: false, data: ERROR_MSG['BRANCH_NOT_FOUND'] }
    }
  }

  async getWaiters(): Promise<any> {
    let branch = this.currentBranch.value;
    if (branch) {
      let branchWaiterQuery = new Parse.Query(this.branchWaitersCollection);
      branchWaiterQuery.equalTo('deleted', false);
      branchWaiterQuery.containedIn('branch', [branch.objectId]);
      branchWaiterQuery.include('waiter');
      branchWaiterQuery.include('branch');

      let branchRoomQuery = new Parse.Query(this.branchRoomsCollection);
      branchRoomQuery.equalTo('deleted', false);

      let waiterRoomQuery = new Parse.Query(this.waiterRoomCollection);
      waiterRoomQuery.matchesKeyInQuery('waiter', 'objectId', branchWaiterQuery);
      waiterRoomQuery.matchesKeyInQuery('room', 'objectId', branchRoomQuery);
      waiterRoomQuery.include('room');
      waiterRoomQuery.ascending('day');
      waiterRoomQuery.addAscending('start');

      let waiterRoomDict = {};
      (await waiterRoomQuery.find()).forEach(waiterRoom => {
        if (!waiterRoomDict[waiterRoom.get('waiter').id]) {
          waiterRoomDict[waiterRoom.get('waiter').id] = {
            [waiterRoom.get('day')]: [{
              objectId: waiterRoom.id,
              day: waiterRoom.get('day'),
              start: waiterRoom.get('start'),
              end: waiterRoom.get('end'),
              room: waiterRoom.get('room').toJSON(),
            }]
          }
        }
        else {
          if (!waiterRoomDict[waiterRoom.get('waiter').id][waiterRoom.get('day')]) {
            waiterRoomDict[waiterRoom.get('waiter').id][waiterRoom.get('day')] = [{
              objectId: waiterRoom.id,
              day: waiterRoom.get('day'),
              start: waiterRoom.get('start'),
              end: waiterRoom.get('end'),
              room: waiterRoom.get('room').toJSON(),
            }]
          }
          else {
            waiterRoomDict[waiterRoom.get('waiter').id][waiterRoom.get('day')].push({
              objectId: waiterRoom.id,
              day: waiterRoom.get('day'),
              start: waiterRoom.get('start'),
              end: waiterRoom.get('end'),
              room: waiterRoom.get('room').toJSON(),
            });
          }
        }
      });

      let waiters = {};
      (await branchWaiterQuery.findAll()).forEach(branchWaiter => {
        let waiter = branchWaiter.toJSON();
        delete waiter['ACL'];
        let workLapses = waiterRoomDict[branchWaiter.id] ?? {};
        waiters[branchWaiter.id] = { ...waiter, workLapses: workLapses };
      })


      return { success: true, data: Object.values(waiters) };
    }
    else {
      return { success: false, data: ERROR_MSG['BRANCH_NOT_FOUND'] };
    }
  }

  async disableWaiter(branchWaiterId: string): Promise<any> {
    let branch = this.currentBranch.value;
    if (branch) {
      return Parse.Cloud.run('disableWaiter', { branchWaiterId: branchWaiterId }).then(result => {
        return result;
      })
    }
    else {
      return { success: false, data: ERROR_MSG['BRANCH_NOT_FOUND'] };
    }
  }

  async editWaiter(branchWaiterid: string, newUser: User): Promise<any> {
    let branch = this.currentBranch.value;
    if (branch) {
      return Parse.Cloud.run('editWaiter', { branchWaiterId: branchWaiterid, user: newUser }).then(result => {
        return result;
      })
    }
    else {
      return { success: false, data: ERROR_MSG['BRANCH_NOT_FOUND'] };
    }
  }

  /**
   * create a worklapse asigned into a waiter and a room
   * @param waiterId branchWaiterId to asign the lapse time
   * @param roomId branchRoomId to asign the lapse time
   * @param day number between 0 and 6 that represent the day, starting from sunday
   * @param start HH:MM time format that represent the start of the lapse time
   * @param end HH:MM time format that represent the end of the lapse time
   * @returns {Promise} with the object created waiter Room lapse created.
   */
  async createWaiterRoomLapse(waiterId: string, roomId: string, day: number, start: string, end: string): Promise<any> {
    let branch = this.currentBranch.value;
    if (branch) {
      return Parse.Cloud.run('createWaiterRoomLapse', { waiterId: waiterId, roomId: roomId, day: day, start: start, end: end }).then(result => {
        return result;
      })
    }
    else {
      return { success: false, data: ERROR_MSG['BRANCH_NOT_FOUND'] }
    }
  }

  async removeWaiterRoomLapse(waiterRoomId: string): Promise<any> {
    let branch = this.currentBranch.value;
    if (branch) {
      let waiterRoomQuery = new Parse.Query(this.waiterRoomCollection);
      return waiterRoomQuery.get(waiterRoomId).then(waiterRoom => {
        if (waiterRoom) {
          return waiterRoom.destroy().then(destroyed => {
            if (destroyed) {
              return { success: true, data: destroyed.toJSON() };
            }
          }, () => {
            return { success: false, data: ERROR_MSG['WORKLAPSE_CANT_DELETED'] };
          })
        }
      }, () => {
        return { success: false, data: ERROR_MSG['WORKLAPSE_NOT_FOUND'] };
      })
    }
    else {
      return { success: false, data: ERROR_MSG['BRANCH_NOT_FOUND'] };
    }
  }

  async getDrivers(): Promise<any> {
    let branch = this.currentBranch.value;
    if (branch) {
      let branchDriverQuery = new Parse.Query(this.branchDriversCollection);
      branchDriverQuery.equalTo('deleted', false);
      branchDriverQuery.containedIn('branch', [branch.objectId]);

      let drivers = (await branchDriverQuery.find()).map(driver => driver.toJSON());

      return { success: true, data: Object.values(drivers) };
    }
    else {
      return { success: false, data: ERROR_MSG['BRANCH_NOT_FOUND'] };
    }
  }

  createDriver(name: string, phone: string) {
    let branch = this.currentBranch.value;
    if (branch) {
      let parseBranch = new this.branchesCollection();
      parseBranch.id = branch.objectId;

      let parseDriver = new this.branchDriversCollection();
      parseDriver.set('branch', parseBranch);
      parseDriver.set('name', name);
      parseDriver.set('phone', phone);
      parseDriver.set('deleted', false);

      return parseDriver.save().then((driver) => {
        return { success: true, data: driver.toJSON() };
      }, (error) => {
        return { success: false, data: ERROR_MSG['SERVER_ERROR'] };
      });
    }
    else {
      return { success: false, data: ERROR_MSG['BRANCH_NOT_FOUND'] };
    }
  }

  async updateDriver(id: string, name: string, phone: string): Promise<any> {
    let branch = this.currentBranch.value;
    if (branch) {
      let parseBranch = new this.branchesCollection();
      parseBranch.id = branch.objectId;

      let driverQuery = new Parse.Query(this.branchDriversCollection);
      driverQuery.equalTo('deleted', false);
      driverQuery.equalTo('branch', parseBranch);
      driverQuery.containedIn('objectId', [id]);

      let driver = await driverQuery.first();
      if (driver) {
        driver.set('name', name);
        driver.set('phone', phone);

        await driver.save();
        return { success: true, data: driver.toJSON() };
      } else {
        return { success: false, data: ERROR_MSG['DRIVER_NOT_FOUND'] };
      }
    }
    else {
      return { success: false, data: ERROR_MSG['BRANCH_NOT_FOUND'] };
    }
  }

  async deleteDriver(id: string): Promise<any> {
    let branch = this.currentBranch.value;
    if (branch) {
      let parseBranch = new this.branchesCollection();
      parseBranch.id = branch.objectId;

      let driverQuery = new Parse.Query(this.branchDriversCollection);
      driverQuery.equalTo('deleted', false);
      driverQuery.equalTo('branch', parseBranch);
      driverQuery.containedIn('objectId', [id]);

      let driver = await driverQuery.first();
      if (driver) {
        driver.set('deleted', true);

        await driver.save();
        return { success: true, data: driver.toJSON() };
      } else {
        return { success: false, data: ERROR_MSG['DRIVER_NOT_FOUND'] };
      }
    }
    else {
      return { success: false, data: ERROR_MSG['BRANCH_NOT_FOUND'] };
    }
  }

  async branchScheduleAvailability(): Promise<any> {
    let branch = this.currentBranch.value;
    if (branch) {
      let branchSchedulesQuery = new Parse.Query(this.branchSchedulesCollection);
      branchSchedulesQuery.containedIn('branch', [branch.objectId]);
      let res = (await branchSchedulesQuery.findAll()).some(schedule => {
        return this._validateSchedule(schedule.get('start'), schedule.get('end'), schedule.get('day'))
      });
      return { success: true, data: res };
    } else {
      return { success: false, data: ERROR_MSG['BRANCH_NOT_FOUND'] };
    }
  }

  async waiterScheduleAvailability(): Promise<any> {
    let branch = this.currentBranch.value;
    if (branch) {
      let user = Parse.User.current();
      // branch Waiter Query
      let branchWaitersQuery = new Parse.Query(this.branchWaitersCollection);
      branchWaitersQuery.containedIn('branch', [branch.objectId]);
      branchWaitersQuery.containedIn('waiter', [user.id]);

      return branchWaitersQuery.first().then(async branchWaiter => {
        if (branchWaiter) {
          // waiter Room query
          let waiterRoomsQuery = new Parse.Query(this.waiterRoomCollection);
          waiterRoomsQuery.equalTo('waiter', branchWaiter);

          let res = (await waiterRoomsQuery.findAll()).some(schedule => {
            return this._validateSchedule(schedule.get('start'), schedule.get('end'), schedule.get('day'))
          });
          return { success: true, data: { isWaiter: true, available: res } };
        } else {
          return { success: true, data: { isWaiter: false } };
        }
      });
    } else {
      return { success: false, data: ERROR_MSG['BRANCH_NOT_FOUND'] };
    }
  }

  freeTable(tableId: string): Promise<any> {
    return Parse.Cloud.run('freeTable', { table: tableId });
  }

  async getSettings(): Promise<any> {
    let settingsQuery = new Parse.Query(this.settingsCollection);

    let settings = (await settingsQuery.find()).map(settings => settings.toJSON());

    return { success: true, data: Object.values(settings) };
  }

  async stopLiveQueryBranchTables(): Promise<void> {
    if (this.liveQueryBranchTables) {
      this.liveQueryBranchTables.unsubscribe();
    }
    if (this.currentBranchSubscription) {
      this.currentBranchSubscription.unsubscribe();
    }
  }

  async startLiveQueryBranchTables(): Promise<void> {
    if (this.currentBranchSubscription) {
      // close old subscription to create new one
      this.currentBranchSubscription.unsubscribe();
    }
    this.currentBranchSubscription = this.currentBranch.subscribe(async (branch) => {
      if (branch) {
        let roomQuery = new Parse.Query(this.branchRoomsCollection);
        roomQuery.equalTo('deleted', false);
        roomQuery.containedIn('branch', [branch.objectId]);

        let rooms = await roomQuery.findAll();

        let tableQuery = new Parse.Query(this.roomTablesCollection);
        tableQuery.equalTo('deleted', false);
        tableQuery.containedIn('room', rooms);
        tableQuery.exists('serviceCall');

        this.liveQueryBranchTables = await tableQuery.subscribe();

        this.liveQueryBranchTables.on('open', async () => {
          let tables = await tableQuery.findAll();
          let tableCalls = {};
          for (let table of tables) {
            tableCalls[table.id] = table.toJSON();
          }
          this.tableCalls.next(tableCalls);
        });

        this.liveQueryBranchTables.on('enter', (table) => {
          let tableCalls = this.tableCalls.value;
          tableCalls[table.id] = table.toJSON();
          this.tableCalls.next(tableCalls);
        });

        this.liveQueryBranchTables.on('create', (table) => {
          let tableCalls = this.tableCalls.value;
          tableCalls[table.id] = table.toJSON();
          this.tableCalls.next(tableCalls);
        });

        this.liveQueryBranchTables.on('leave', (table) => {
          let tableCalls = this.tableCalls.value;
          delete tableCalls[table.id];
          this.tableCalls.next(tableCalls);
        });

        this.liveQueryBranchTables.on('update', (table) => {
          let tableCalls = this.tableCalls.value;
          tableCalls[table.id] = table.toJSON();
          this.tableCalls.next(tableCalls);
        });
      }
    });
  }

  markTableAsAttended(tableId: string) {
    let tableQuery = new Parse.Query(this.roomTablesCollection);
    tableQuery.get(tableId).then(async (table) => {
      table.unset('serviceCall');
      await table.save();
      return { success: true };
    }).catch((error) => {
      return { success: false, data: ERROR_MSG['TABLE_NOT_FOUND'] };
    });
  }
}
